import React, { useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, message, Space, Popconfirm, Card } from 'antd';
import { PlusOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import { printInvoice } from '../utils/printInvoice';
import { printIncomeReport, formatMoney, getIncomeName, sumByCurrency, groupByName } from '../utils/printIncomeReport';
import dayjs from 'dayjs';

const Income = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuthStore();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: incomeData, isLoading } = useQuery('income', async () => {
        const response = await api.get('/income', {
            params: { limit: 10000 }
        });
        return response.data.data;
    });

    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Distinct descriptions of "other" records, offered as extra category filters
    const otherDescriptions = useMemo(() => {
        const set = new Set();
        (incomeData || []).forEach((r) => {
            const d = (r.description || '').trim();
            if (r.category === 'other' && d) set.add(d);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [incomeData]);

    const filteredData = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return (incomeData || []).filter((r) => {
            if (categoryFilter) {
                if (categoryFilter.startsWith('desc:')) {
                    if (r.category !== 'other' || (r.description || '').trim() !== categoryFilter.slice(5)) return false;
                } else if (r.category !== categoryFilter) {
                    return false;
                }
            }
            if (dateRange && dateRange[0] && dateRange[1]) {
                const d = dayjs(r.date);
                if (d.isBefore(dateRange[0].startOf('day')) || d.isAfter(dateRange[1].endOf('day'))) return false;
            }
            if (q) {
                const hay = [getIncomeName(r), r.receiptNumber, r.description, r.category]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [incomeData, searchText, categoryFilter, dateRange]);

    // Selected rows (only those still visible after filtering)
    const selectedRecords = useMemo(
        () => filteredData.filter((r) => selectedRowKeys.includes(r._id)),
        [filteredData, selectedRowKeys]
    );
    const summaryRecords = selectedRecords.length ? selectedRecords : filteredData;
    const totalsByCurrency = useMemo(() => sumByCurrency(summaryRecords), [summaryRecords]);
    const totalsByName = useMemo(() => groupByName(summaryRecords), [summaryRecords]);
    const formatTotals = (totals) =>
        Object.entries(totals).map(([cur, val]) => formatMoney(val, cur)).join(' + ') || '-';

    const handlePrintReport = () => {
        const ok = printIncomeReport({
            records: summaryRecords,
            user,
            t,
            language: i18n.language,
            title: t('incomeReportTitle'),
            dateRange: dateRange && dateRange[0] && dateRange[1]
                ? `${dateRange[0].format('YYYY-MM-DD')} - ${dateRange[1].format('YYYY-MM-DD')}`
                : '',
        });
        if (!ok) message.error(t('failedToPrint') || 'Popup blocked');
    };

    const handlePrint = (record) => {
        printInvoice({
            invoice: record,
            student: record.student,
            user,
            t,
            language: i18n.language
        });
    };

    const createMutation = useMutation(
        (data) => api.post('/income', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('income');
                message.success(t('incomeRecordAdded'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToAddIncome'));
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/income/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('income');
                message.success(t('updatedSuccessfully'));
                setIsModalVisible(false);
                setEditingIncome(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdate'));
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/income/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('income');
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToDelete'));
            },
        }
    );

    const columns = [
        { title: t('date'), dataIndex: 'date', key: 'date', render: (date) => dayjs(date).format('YYYY-MM-DD') },
        { title: t('receiptNo'), dataIndex: 'receiptNumber', key: 'receiptNumber' },
        {
            title: t('category'),
            key: 'category',
            render: (_, r) => (r.category === 'other' && r.description?.trim() ? r.description.trim() : t(r.category, { defaultValue: r.category })),
        },
        { title: t('description'), dataIndex: 'description', key: 'description' },
        {
            title: t('amount'), dataIndex: 'amount', key: 'amount', render: (amount, record) => {
                const curr = record.currency || 'USD';
                return curr === 'IQD' ? `IQD ${amount.toLocaleString()}` : `$${amount.toFixed(2)}`;
            }
        },
        { title: t('student'), key: 'student', render: (_, r) => r.student ? `${r.student.firstName} ${r.student.lastName}` : '-' },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrint(record)}
                        size="small"
                    >
                        {t('print')}
                    </Button>
                    {user?.role === 'admin' && (
                        <>
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setEditingIncome(record);
                                    form.setFieldsValue({
                                        ...record,
                                        date: record.date ? dayjs(record.date) : null
                                    });
                                    setIsModalVisible(true);
                                }}
                            />
                            <Popconfirm
                                title={t('areYouSureDelete')}
                                onConfirm={() => deleteMutation.mutate(record._id)}
                                okText={t('yes')}
                                cancelText={t('no')}
                            >
                                <Button
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleSubmit = (values) => {
        const payload = {
            ...values,
            date: values.date ? values.date.toISOString() : new Date().toISOString(),
        };

        if (editingIncome) {
            updateMutation.mutate({ id: editingIncome._id, data: payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    return (
        <div>
            <div className="page-header">
                <h1>{t('income')}</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                    {t('addIncome')}
                </Button>
            </div>
            <Space wrap style={{ marginBottom: 16 }}>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={t('incomeSearchPlaceholder')}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 280 }}
                />
                <Select
                    allowClear
                    placeholder={t('incomeAllCategories')}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    style={{ width: 170 }}
                >
                    <Select.Option value="tuition">{t('tuition')}</Select.Option>
                    <Select.Option value="registration">{t('registration')}</Select.Option>
                    <Select.Option value="other">{t('other')}</Select.Option>
                    {otherDescriptions.map((d) => (
                        <Select.Option key={`desc:${d}`} value={`desc:${d}`}>{d}</Select.Option>
                    ))}
                </Select>
                <DatePicker.RangePicker value={dateRange} onChange={setDateRange} />
                <Button icon={<PrinterOutlined />} onClick={handlePrintReport} disabled={!summaryRecords.length}>
                    {selectedRecords.length
                        ? `${t('incomePrintSelected')} (${selectedRecords.length})`
                        : t('incomePrintReport')}
                </Button>
            </Space>
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={isLoading}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                scroll={{ x: 'max-content' }}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={5}>
                                <strong>
                                    {t('incomeGrandTotal')}
                                    {selectedRecords.length
                                        ? ` (${t('incomeSelectedCount')}: ${selectedRecords.length})`
                                        : ` (${filteredData.length})`}
                                </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} colSpan={3}>
                                <strong>{formatTotals(totalsByCurrency)}</strong>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
            {totalsByName.length > 0 && (
                <Card size="small" title={t('incomeTotalsByName')} style={{ marginTop: 16 }}>
                    <Table
                        size="small"
                        pagination={{ pageSize: 10, hideOnSinglePage: true }}
                        rowKey="name"
                        dataSource={totalsByName}
                        columns={[
                            { title: t('student'), dataIndex: 'name', key: 'name' },
                            { title: t('incomeRecordsCount'), dataIndex: 'count', key: 'count' },
                            { title: t('amount'), key: 'totals', render: (_, g) => formatTotals(g.totals) },
                        ]}
                    />
                </Card>
            )}

            <Modal
                title={editingIncome ? t('editIncome') : t('addIncome')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingIncome(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(500px, 95vw)"
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="date" label={t('date')}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="receiptNumber" label={t('receiptNo')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="category" label={t('category')} rules={[{ required: true, message: t('pleaseSelectCategory') }]}>
                        <Select>
                            <Select.Option value="tuition">{t('tuition')}</Select.Option>
                            <Select.Option value="registration">{t('registration')}</Select.Option>
                            <Select.Option value="other">{t('other')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label={t('description')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('pleaseSelectCurrency') }]} initialValue="USD">
                        <Select>
                            <Select.Option value="USD">USD ($)</Select.Option>
                            <Select.Option value="IQD">IQD (د.ع)</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="amount" label={t('amount')} rules={[{ required: true, message: t('pleaseEnterAmount') }]}>
                        <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingIncome ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingIncome(null);
                                form.resetFields();
                            }}>{t('cancel')}</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Income;
